/**
 * A worker's pay basis — `employee_pay_profiles.pay_basis` (api#1097, 0523).
 *
 * Mirrors the CHECK constraint and `PAY_BASES` in the API's
 * pay-profiles.routes.ts: 'hourly' | 'percentage' | 'day_rate' | 'salary' |
 * 'bol_percent'. 'percentage' means "paid from tickets", the way every casing
 * hand has always been paid; it did not exist before 0523 because a worker
 * with NO profile was paid that way implicitly.
 *
 * ── THE DEFAULT IS THE API'S; PERCENTAGE IS OFFERED EVERYWHERE ─────────────
 * Operator decision, 2026-11-04 (feat/ca-hourly-pay): clock pay is coming to
 * Canadian hourly workers (earthworks crews first), so a Canadian 'hourly'
 * profile will pay from the clock instead of from tickets. Two consequences:
 *
 *   - A NEW profile's basis comes from the API (`GET /pay-profiles/defaults`,
 *     hook `useDefaultPayBasis`): US → Hourly, CA with earthworks → Hourly,
 *     every other tenant → Percentage. Neither screen keeps a default of its own.
 *   - Percentage — "paid from tickets", how every casing hand is paid — is
 *     offered on Canadian tenants too, so an office can say so explicitly.
 *
 *   /payroll/workers (WorkerPayProfileScreen): Percentage · Hourly · Day Rate · Salary
 *   EmployeeProfile's pay dialog:              Hourly · Percentage · BOL Percentage · Salary · Day Rate
 *
 * The lists are the same for every country. The API, not this file, decides
 * which basis actually pays from the clock.
 *
 * ── A STORED BASIS IS ALWAYS SHOWN ──────────────────────────────────────────
 * An existing profile keeps its basis, and a control that has no option for
 * the stored value shows nothing selected while the save still posts it. So
 * each option list is the list PLUS the stored basis when it is not
 * already there — a CA worker saved as 'percentage' still sees Percentage.
 */

export const PAY_BASES = ['hourly', 'percentage', 'day_rate', 'salary', 'bol_percent'] as const;
export type PayBasis = (typeof PAY_BASES)[number];

export const PAY_BASIS_LABELS: Record<PayBasis, string> = {
  hourly: 'Hourly',
  percentage: 'Percentage',
  day_rate: 'Day Rate',
  salary: 'Salary',
  bol_percent: 'BOL Percentage',
};

export function isPayBasis(value: unknown): value is PayBasis {
  return typeof value === 'string' && (PAY_BASES as readonly string[]).includes(value);
}

/** The list, plus a stored basis it does not already contain. */
function withStored(list: readonly PayBasis[], stored: unknown): PayBasis[] {
  return isPayBasis(stored) && !list.includes(stored) ? [...list, stored] : [...list];
}

/** /payroll/workers — the options, in display order. */
export function workerScreenBasisOptions(stored?: unknown): PayBasis[] {
  return withStored(['percentage', 'hourly', 'day_rate', 'salary'], stored);
}

/** EmployeeProfile's pay dialog — the options, in display order. */
export function dialogBasisOptions(stored?: unknown): PayBasis[] {
  return withStored(['hourly', 'percentage', 'bol_percent', 'salary', 'day_rate'], stored);
}

/**
 * The label for a stored basis. A value this build does not know is shown as
 * an em dash rather than echoed raw or guessed (label maps are exhaustive).
 */
export function payBasisLabel(value: unknown): string {
  return isPayBasis(value) ? PAY_BASIS_LABELS[value] : '—';
}

/**
 * A percentage worker is paid from tickets at the percentages on their pay
 * rates, so the profile's base rate prices nothing. The column is NOT NULL, and
 * the API's contract is to send 0 for it (api-b0, 2026-10-16). That 0 is never
 * displayed as a rate — see `formatBaseRate` in EmployeeProfile.tsx.
 */
export const PERCENTAGE_BASE_RATE = 0;
